import { Component, OnInit, ViewChild, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { AddFormComponent } from '@shared/components/add-form/add-form.component';
import {
  MATERIAL_FORM_CONFIG,
  MaterialFormConfig,
  MaterialItem,
  UsageVideoPublishStatus,
  UsageVideoType,
} from '../material.config';
import { MaterialService } from '../services/material.service';
import { UploadConfigIntegrationService } from '../../../../configuration/upload-detail-config/services/upload-config-integration.service';
import { UploadDetailConfig } from '../../../../configuration/upload-detail-config/models/upload-config.model';
import {
  MaterialControllerService,
  MaterialImageReorderDTO,
} from '@app/api/goods-service';

@Component({
  selector: 'app-material-form',
  standalone: true,
  imports: [CommonModule, TranslateModule, ToastModule, AddFormComponent],
  providers: [MessageService],
  templateUrl: './material-form.component.html',
  styleUrls: ['./material-form.component.css'],
})
export class MaterialFormComponent implements OnInit {
  @ViewChild(AddFormComponent) addFormComponent?: AddFormComponent;

  // Form configuration
  config = signal<MaterialFormConfig>(MATERIAL_FORM_CONFIG);

  // Mode flags
  editMode = signal(false);
  viewMode = signal(false);

  // Current material id from the route
  materialId = signal<number | null>(null);

  // Initial data
  initialData = signal<Partial<MaterialItem> | null>(null);

  // Form data management (following SPU pattern)
  formData = signal<any>({});

  // Image data management (following SPU pattern)
  mainImageData = signal<any>(null);
  detailImageData = signal<any>(null);
  detailImagesData = signal<any[]>([]);

  // Track removed images for backend processing
  removedMainImages = signal<string[]>([]);
  removedDetailImages = signal<string[]>([]);

  // Staged drag-drop reorder of detailImages, applied on Save (see onExistingImagesReordered)
  pendingDetailImageReorder = signal<MaterialImageReorderDTO[] | null>(null);

  // Usage-instruction video data management
  usageVideoData = signal<any>(null);
  removeUsageVideo = signal(false);
  usageVideoType = signal<UsageVideoType>('SELF_HOSTED');
  usageVideoPublishStatus = signal<UsageVideoPublishStatus>('DRAFT');

  // Loading state
  loading = signal(false);
  private skipCanDeactivate = false;

  // Upload configuration
  uploadConfig = signal<UploadDetailConfig | null>(null);

  // Computed property to combine all form data for initialData (following SPU pattern)
  combinedInitialData = computed(() => {
    const initialData = this.initialData();
    const currentFormData = this.formData();
    const removedMainImages = this.removedMainImages();

    // If no initial data and no form data, return empty object
    if (!initialData && !currentFormData) {
      return {};
    }

    // Start with initial data as base
    const baseData: any = initialData ? { ...initialData } : {};

    // Override with current form data to preserve user changes
    // Only override if currentFormData has meaningful values (not empty strings)
    // This prevents empty form initialization from overriding initialData
    if (currentFormData && Object.keys(currentFormData).length > 0) {
      // Check if currentFormData is just empty initial state
      const hasMeaningfulData = Object.keys(currentFormData).some((key) => {
        const value = currentFormData[key];
        if (value === undefined || value === null) return false;
        if (typeof value === 'string' && value.trim() === '') return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      });

      // Only merge if currentFormData has meaningful data (user has made changes)
      if (hasMeaningfulData) {
        Object.keys(currentFormData).forEach((key) => {
          // Skip image/video fields - handled separately below. usageVideoType/
          // usageVideoPublishStatus have non-empty defaults, so their currentFormData copies
          // would always win this merge and mask an existing loaded value; the tracking
          // signals below are authoritative instead.
          if (
            key === 'mainImage' ||
            key === 'detailImages' ||
            key === 'usageVideo' ||
            key === 'usageVideoType' ||
            key === 'usageVideoPublishStatus'
          ) {
            return;
          }

          const value = currentFormData[key];
          // Only override if value is meaningful:
          // - Not undefined
          // - Not null
          // - Not empty string (for text fields)
          // - For arrays/objects, check if they have content
          if (value !== undefined && value !== null) {
            // For string values, only override if not empty
            if (typeof value === 'string') {
              if (value.trim() !== '') {
                baseData[key] = value;
              }
            }
            // For arrays, only override if not empty
            else if (Array.isArray(value)) {
              if (value.length > 0) {
                baseData[key] = value;
              }
            }
            // For other types (objects, numbers, booleans), always override
            else {
              baseData[key] = value;
            }
          }
        });
      }
    }

    // See the exclusion above for why currentFormData's usageVideoType/usageVideoPublishStatus
    // can't be trusted here.
    baseData.usageVideoType = this.usageVideoType();
    baseData.usageVideoPublishStatus = this.usageVideoPublishStatus();

    // Check if currentFormData is empty initial state (all text fields are empty)
    const isCurrentFormDataEmpty =
      currentFormData && Object.keys(currentFormData).length > 0
        ? Object.keys(currentFormData).every((key) => {
            const value = currentFormData[key];
            if (
              key === 'mainImage' ||
              key === 'detailImages' ||
              key === 'usageVideo'
            )
              return false; // Skip image/video fields
            if (value === undefined || value === null) return true;
            if (typeof value === 'string' && value.trim() === '') return true;
            if (Array.isArray(value) && value.length === 0) return true;
            return false;
          })
        : true;

    // Handle mainImage - prioritize current form data, then initial data, then signal
    // But only use currentFormData.mainImage if currentFormData is not empty initial state
    // NOTE: FileUploadComponent expects a single object, not an array, even if field has multiple: true
    let mainImageValue: any = null;
    const initialMainImage = initialData?.mainImage;
    const initialMainImageFileName = Array.isArray(initialMainImage)
      ? initialMainImage[0]?.fileName
      : initialMainImage?.fileName;
    const isInitialMainImageRemoved = !!(
      initialMainImageFileName &&
      removedMainImages.includes(initialMainImageFileName)
    );

    const currentMainImageFileName = currentFormData?.mainImage?.fileName;
    const isCurrentMainImageRemoved = !!(
      currentFormData?.mainImage?.isExisting &&
      currentMainImageFileName &&
      removedMainImages.includes(currentMainImageFileName)
    );

    if (
      currentFormData?.mainImage &&
      !isCurrentFormDataEmpty &&
      !isCurrentMainImageRemoved
    ) {
      mainImageValue = currentFormData.mainImage;
    } else if (initialData?.mainImage && !isInitialMainImageRemoved) {
      mainImageValue = initialData.mainImage;
    } else {
      const mainImageData = this.mainImageData();
      if (mainImageData) {
        mainImageValue = mainImageData;
      }
    }

    // Convert array to single object for FileUploadComponent (it expects single object, not array)
    if (mainImageValue) {
      if (Array.isArray(mainImageValue) && mainImageValue.length > 0) {
        baseData.mainImage = mainImageValue[0]; // Take first element for file type field
      } else {
        baseData.mainImage = mainImageValue;
      }
    } else {
      delete baseData.mainImage;
    }

    // Handle detailImages - prioritize current form data, then initial data, then signal
    // But only use currentFormData.detailImages if:
    // 1. currentFormData is not empty initial state AND
    // 2. currentFormData.detailImages has actual content (not empty array)
    const hasCurrentDetailImages =
      currentFormData?.detailImages &&
      Array.isArray(currentFormData.detailImages) &&
      currentFormData.detailImages.length > 0;

    const hasInitialDetailImages =
      initialData?.detailImages &&
      Array.isArray(initialData.detailImages) &&
      initialData.detailImages.length > 0;

    if (hasCurrentDetailImages && !isCurrentFormDataEmpty) {
      baseData.detailImages = currentFormData.detailImages;
    } else if (hasInitialDetailImages) {
      baseData.detailImages = initialData.detailImages;
    } else {
      const detailImagesData = this.detailImagesData();
      if (detailImagesData && detailImagesData.length > 0) {
        baseData.detailImages = detailImagesData;
      } else {
        // If no detail images from any source, set empty array
        baseData.detailImages = [];
      }
    }

    // Handle usageVideo - prioritize current form data, then
    // initial data, then signal; a single removeUsageVideo flag (not
    // filename-based tracking) is enough since there is only one video.
    const isUsageVideoRemoved = this.removeUsageVideo();
    let usageVideoValue: any = null;

    if (
      currentFormData?.usageVideo &&
      !isCurrentFormDataEmpty &&
      !isUsageVideoRemoved
    ) {
      usageVideoValue = currentFormData.usageVideo;
    } else if (initialData?.usageVideo && !isUsageVideoRemoved) {
      usageVideoValue = initialData.usageVideo;
    } else if (!isUsageVideoRemoved) {
      const usageVideoData = this.usageVideoData();
      if (usageVideoData) {
        usageVideoValue = usageVideoData;
      }
    }

    if (usageVideoValue) {
      baseData.usageVideo = usageVideoValue;
    } else {
      delete baseData.usageVideo;
    }

    return baseData;
  });

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly materialService: MaterialService,
    private readonly materialControllerService: MaterialControllerService,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
    private readonly uploadConfigIntegration: UploadConfigIntegrationService,
  ) {}

  ngOnInit(): void {
    this.determineMode();
    this.loadMaterialData();
    this.loadUploadConfiguration();
  }

  private determineMode(): void {
    const url = this.router.url;

    if (url.includes('/add')) {
      this.editMode.set(false);
      this.viewMode.set(false);
      this.updateTitle('material.headers.add_product_asset');
    } else if (url.includes('/edit')) {
      this.editMode.set(true);
      this.viewMode.set(false);
      this.updateTitle('material.headers.edit_product_asset');
    } else if (url.includes('/view')) {
      this.editMode.set(false);
      this.viewMode.set(true);
      this.updateTitle('material.headers.view_product_asset');
    }
  }

  private updateTitle(titleKey: string): void {
    const currentConfig = this.config();
    this.config.set({
      ...currentConfig,
      title: titleKey,
    });
  }

  private loadMaterialData(): void {
    if (this.editMode() || this.viewMode()) {
      const materialId = this.route.snapshot.queryParams['id'];
      this.materialId.set(materialId ? Number(materialId) : null);

      if (materialId) {
        this.loading.set(true);

        this.materialService.getMaterialById(materialId).subscribe({
          next: (material) => {
            if (material) {
              // Prepare form data object - handle null/undefined values
              const formDataToSet: any = {
                materialName: material.materialName ?? '',
                code: material.code ?? '',
                detailHtml: material.detailHtml ?? '',
                remark: material.remark ?? '',
                status: material.status ?? true,
              };

              // Handle main image - convert to array format for form
              // Priority: 1) mainImage from service, 2) mainImageUrls array
              let mainImageProcessed = false;

              if (material.mainImage) {
                if (Array.isArray(material.mainImage)) {
                  // Already an array - take first element (FileUploadComponent expects single object)
                  const fileName =
                    material.mainImage[0].fileName ||
                    this.extractFileNameFromUrl(
                      material.mainImage[0].previewUrl || '',
                    );
                  // Create dummy File object for existing images (same as FileUploadComponent does)
                  const dummyFile =
                    material.mainImage[0].previewUrl &&
                    !material.mainImage[0].file
                      ? new File([], fileName, { type: 'image/jpeg' })
                      : null;
                  const mainImageData = {
                    file: dummyFile,
                    previewUrl: material.mainImage[0].previewUrl || '',
                    fileName: fileName,
                    fileSize: material.mainImage[0].fileSize || 0,
                    isExisting: true,
                  };
                  formDataToSet.mainImage = mainImageData; // Store as single object, not array
                  this.mainImageData.set(mainImageData);
                  mainImageProcessed = true;
                } else if (material.mainImage.previewUrl) {
                  // Single object - store as single object (FileUploadComponent expects single object, not array)
                  const fileName =
                    material.mainImage.fileName ||
                    this.extractFileNameFromUrl(
                      material.mainImage.previewUrl || '',
                    );
                  // Create dummy File object for existing images (same as FileUploadComponent does)
                  const dummyFile = new File([], fileName, {
                    type: 'image/jpeg',
                  });
                  const mainImageData = {
                    file: dummyFile,
                    previewUrl: material.mainImage.previewUrl || '',
                    fileName: fileName,
                    fileSize: material.mainImage.fileSize || 0,
                    isExisting: true,
                  };
                  formDataToSet.mainImage = mainImageData; // Store as single object, not array
                  this.mainImageData.set(mainImageData);
                  mainImageProcessed = true;
                } else {
                }
              }

              // Fallback: handle mainImageUrls directly from API response
              if (
                !mainImageProcessed &&
                material.mainImageUrls &&
                Array.isArray(material.mainImageUrls) &&
                material.mainImageUrls.length > 0
              ) {
                // Take first URL (FileUploadComponent expects single object, not array)
                const fileName = this.extractFileNameFromUrl(
                  material.mainImageUrls[0],
                );
                // Create dummy File object for existing images (same as FileUploadComponent does)
                const dummyFile = new File([], fileName, {
                  type: 'image/jpeg',
                });
                const mainImageData = {
                  file: dummyFile,
                  previewUrl: material.mainImageUrls[0],
                  fileName: fileName,
                  fileSize: 0,
                  isExisting: true,
                };
                formDataToSet.mainImage = mainImageData; // Store as single object, not array
                this.mainImageData.set(mainImageData);
              }

              // Handle detail images - ensure it's an array
              // Priority: 1) detailImages from service, 2) detailImageUrls array, 3) detailImage (legacy)
              let detailImagesProcessed = false;

              if (
                material.detailImages &&
                Array.isArray(material.detailImages) &&
                material.detailImages.length > 0
              ) {
                // Already an array from service
                const detailImagesData = material.detailImages.map(
                  (img, index) => {
                    const fileName =
                      img.fileName ||
                      this.extractFileNameFromUrl(img.previewUrl || '');
                    // Create dummy File object for existing images (same as FileUploadComponent does)
                    const dummyFile =
                      img.previewUrl && !img.file
                        ? new File([], fileName, { type: 'image/jpeg' })
                        : null;
                    return {
                      id: `existing-${index}`, // Add id for MultiFileUploadComponent tracking
                      file: dummyFile, // Create dummy file for display (same as main image)
                      previewUrl: img.previewUrl || '',
                      fileName: fileName,
                      fileSize: img.fileSize || 0,
                      isExisting: true,
                      onFileChanged: false, // Mark as not changed
                    };
                  },
                );
                formDataToSet.detailImages = detailImagesData;
                this.detailImagesData.set(detailImagesData);
                if (detailImagesData.length > 0) {
                  this.detailImageData.set(detailImagesData[0]);
                }
                detailImagesProcessed = true;
              } else if (
                material.detailImageUrls &&
                Array.isArray(material.detailImageUrls) &&
                material.detailImageUrls.length > 0
              ) {
                // Fallback: handle detailImageUrls directly from API response
                const detailImagesData = material.detailImageUrls.map(
                  (url: string, index: number) => {
                    const fileName = this.extractFileNameFromUrl(url);
                    // Create dummy File object for existing images (same as FileUploadComponent does)
                    const dummyFile = new File([], fileName, {
                      type: 'image/jpeg',
                    });
                    return {
                      id: `existing-${index}`, // Add id for MultiFileUploadComponent tracking
                      file: dummyFile, // Create dummy file for display (same as main image)
                      previewUrl: url,
                      fileName: fileName,
                      fileSize: 0, // Size not available from URL
                      isExisting: true,
                      onFileChanged: false, // Mark as not changed
                    };
                  },
                );
                formDataToSet.detailImages = detailImagesData;
                this.detailImagesData.set(detailImagesData);
                if (detailImagesData.length > 0) {
                  this.detailImageData.set(detailImagesData[0]);
                }
                detailImagesProcessed = true;
              } else if (
                material.detailImage &&
                material.detailImage.previewUrl
              ) {
                // Legacy support for single detail image - convert to array
                const fileName =
                  material.detailImage.fileName ||
                  this.extractFileNameFromUrl(
                    material.detailImage.previewUrl || '',
                  );
                // Create dummy File object for existing images (same as FileUploadComponent does)
                const dummyFile = new File([], fileName, {
                  type: 'image/jpeg',
                });
                const detailImageData = {
                  id: 'existing-0', // Add id for MultiFileUploadComponent tracking
                  file: dummyFile, // Create dummy file for display (same as main image)
                  previewUrl: material.detailImage.previewUrl || '',
                  fileName: fileName,
                  fileSize: material.detailImage.fileSize || 0,
                  isExisting: true,
                  onFileChanged: false, // Mark as not changed
                };
                formDataToSet.detailImages = [detailImageData];
                this.detailImagesData.set([detailImageData]);
                this.detailImageData.set(detailImageData);
                detailImagesProcessed = true;
              }

              // No detail images - set empty array
              if (!detailImagesProcessed) {
                formDataToSet.detailImages = [];
                this.detailImagesData.set([]);
              }

              // Usage-instruction video
              const usageVideoPublishStatus =
                material.usageVideoPublishStatus ?? 'DRAFT';
              formDataToSet.usageVideoPublishStatus = usageVideoPublishStatus;
              this.usageVideoPublishStatus.set(usageVideoPublishStatus);
              formDataToSet.usageVideo = material.usageVideo ?? null;
              this.usageVideoData.set(material.usageVideo ?? null);

              const usageVideoType = material.usageVideoType ?? 'SELF_HOSTED';
              formDataToSet.usageVideoType = usageVideoType;
              formDataToSet.usageVideoExternalUrl =
                material.usageVideoExternalUrl ?? '';
              this.usageVideoType.set(usageVideoType);
              // app-add-form binds [formFields] before [initialData], so calling this
              // synchronously would rebuild the form (via the hidden/disabled changes below)
              // before initialData's setter has populated the child's _initialData cache,
              // reverting usageVideoType to the field's default. Defer past this CD cycle.
              setTimeout(() => this.applyUsageVideoTypeVisibility(usageVideoType));
              // Defensive final correction: add-form's own internal patch/rebuild sequencing
              // can still revert the control after everything settles, even with the deferral
              // above - re-assert the loaded value directly once those steps have finished.
              setTimeout(() => {
                const control = this.addFormComponent?.form?.get('usageVideoType');
                if (control && control.value !== usageVideoType) {
                  control.setValue(usageVideoType);
                  this.usageVideoType.set(usageVideoType);
                  this.applyUsageVideoTypeVisibility(usageVideoType);
                }
                // Same defensive re-assertion as usageVideoType above, for the same reason.
                const publishStatusControl = this.addFormComponent?.form?.get(
                  'usageVideoPublishStatus',
                );
                if (
                  publishStatusControl &&
                  publishStatusControl.value !== usageVideoPublishStatus
                ) {
                  publishStatusControl.setValue(usageVideoPublishStatus);
                  this.usageVideoPublishStatus.set(usageVideoPublishStatus);
                }
              }, 500);

              // Set initial data with all form fields including images
              // Clear formData if it's just empty initial state to prevent it from overriding initialData
              const currentFormData = this.formData();
              if (currentFormData && Object.keys(currentFormData).length > 0) {
                // Check if currentFormData is just empty initial state
                const isEmptyState = Object.keys(currentFormData).every(
                  (key) => {
                    const value = currentFormData[key];
                    if (value === undefined || value === null) return true;
                    if (typeof value === 'string' && value.trim() === '')
                      return true;
                    if (Array.isArray(value) && value.length === 0) return true;
                    return false;
                  },
                );

                if (isEmptyState) {
                  this.formData.set({});
                }
              }

              this.initialData.set(formDataToSet);
            } else {
              console.error('Product asset not found');
              this.router.navigate([this.config().listRoute]);
            }
            this.loading.set(false);
          },
          error: (error) => {
            console.error('Error loading product asset:', error);
            this.loading.set(false);
            this.router.navigate([this.config().listRoute]);
          },
        });
      } else {
        console.error('No material ID provided');
        this.router.navigate([this.config().listRoute]);
      }
    }
  }

  onFormAction(event: { action: string; data?: any }): void {
    switch (event.action) {
      case 'save':
        // Prevent multiple API calls if already saving
        if (this.loading()) {
          return;
        }
        if (this.addFormComponent?.form?.invalid) {
          return;
        }
        // Set loading state immediately to prevent race conditions
        this.loading.set(true);
        this.saveMaterial(event.data);
        break;
      case 'cancel':
        globalThis.history.back();
        break;
      default:
        // Unknown action
        break;
    }
  }

  canDeactivate(): boolean {
    if (this.viewMode() || this.skipCanDeactivate || this.loading()) {
      return true;
    }

    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  private saveMaterial(formData: any): void {
    // Use the processed data from AddFormComponent (already filtered for changed files)
    const processedData = { ...formData };

    if (this.editMode()) {
      const materialId = this.route.snapshot.queryParams['id'];
      const removedMainImages = this.removedMainImages();
      const removedDetailImages = this.removedDetailImages();

      if (removedMainImages.length > 0) {
        processedData.removedMainImages = Array.from(
          new Set([
            ...(processedData.removedMainImages ?? []),
            ...removedMainImages,
          ]),
        );
      }

      if (removedDetailImages.length > 0) {
        processedData.removedDetailImages = Array.from(
          new Set([
            ...(processedData.removedDetailImages ?? []),
            ...removedDetailImages,
          ]),
        );
      }

      if (this.removeUsageVideo()) {
        processedData.removeUsageVideo = true;
      }

      this.materialService.updateMaterial(materialId, processedData).subscribe({
        next: (updatedMaterial) => {
          this.loading.set(false); // Reset loading state
          if (updatedMaterial) {
            this.persistPendingDetailImageReorder(Number(materialId));
            this.skipCanDeactivate = true;
            this.addFormComponent?.form?.markAsPristine();
            this.messageService.add({
              severity: 'success',
              summary: this.interpolateMessage('common.actions.success'),
              detail: this.interpolateMessage(
                'material.messages.success_updating_product_assets',
              ),
            });
            setTimeout(() => {
              // Remove the 'id' parameter when navigating back to list page
              const { id, ...filterParams } = this.route.snapshot.queryParams;

              // Navigate back to the list page with preserved filter parameters
              this.router.navigate(['/goods/material'], {
                queryParams: filterParams,
              });
            }, 1000);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.interpolateMessage('common.actions.error'),
              detail: this.interpolateMessage(
                'material.messages.error_updating_product_assets',
              ),
            });
          }
        },
        error: (error) => {
          console.error('Error updating product asset:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.interpolateMessage('common.actions.error'),
            detail: this.resolveUsageVideoErrorMessage(
              error,
              'material.messages.error_updating_product_assets',
            ),
          });
          this.loading.set(false);
        },
      });
    } else {
      this.materialService.createMaterial(processedData).subscribe({
        next: (newMaterial) => {
          this.loading.set(false); // Reset loading state
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.interpolateMessage('common.actions.success'),
            detail: this.interpolateMessage(
              'material.messages.success_creating_product_assets',
            ),
          });
          // Navigate after a small delay to allow toast to be visible
          setTimeout(() => {
            this.router.navigate(['/goods/material']);
          }, 1000);
        },
        error: (error) => {
          console.error('Error creating product asset:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.interpolateMessage('common.actions.error'),
            detail: this.resolveUsageVideoErrorMessage(
              error,
              'material.messages.error_creating_product_assets',
            ),
          });
          this.loading.set(false);
        },
      });
    }
  }

  private resolveUsageVideoErrorMessage(
    error: any,
    fallbackKey: string,
  ): string {
    const errorCode = error?.error?.errorCode;
    if (errorCode === 'INVALID_VIDEO_FORMAT') {
      return this.interpolateMessage(
        'material.messages.error_invalid_video_format',
      );
    }
    if (errorCode === 'CONCURRENT_MODIFICATION') {
      return this.interpolateMessage('common.messages.concurrent_modification');
    }
    if (errorCode === 'MATERIAL_CODE_ALREADY_EXISTS') {
      return this.interpolateMessage(
        'material.messages.error_code_already_exists',
      );
    }
    if (errorCode === 'MATERIAL_NAME_ALREADY_EXISTS') {
      return this.interpolateMessage(
        'material.messages.error_name_already_exists',
      );
    }
    return this.interpolateMessage(fallbackKey);
  }

  private processFormData(formData: any): Partial<MaterialItem> {
    const processedData: Partial<MaterialItem> = {
      materialName: formData.materialName,
      code: formData.code,
      detailHtml: formData.detailHtml,
      remark: formData.remark,
      status: formData.status,
    };

    // In edit mode, handle image logic based on changes
    if (this.editMode()) {
      // Handle main image
      const mainImageData = this.mainImageData();
      if (mainImageData) {
        if (mainImageData.isExisting && !mainImageData.removed) {
          // Existing image not changed - don't send image data
          processedData.mainImage = undefined;
        } else if (mainImageData.file && !mainImageData.isExisting) {
          // New image uploaded
          processedData.mainImage = {
            previewUrl: mainImageData.previewUrl,
            fileName: mainImageData.fileName,
            fileSize: mainImageData.fileSize,
            file: mainImageData.file,
          };
        }
      }

      // Handle detail images (indexed controls)
      const detailImages: any[] = [];
      const maxFiles =
        this.uploadConfigIntegration.getMaxFilesForDetailImages() || 5;

      for (let i = 0; i < maxFiles; i++) {
        const controlName = `detailImage_${i}`;
        const imageData = formData[controlName];

        if (imageData) {
          if (imageData.isExisting && !imageData.removed) {
            // Existing image not changed - include it in the array
            detailImages.push({
              previewUrl: imageData.previewUrl,
              fileName: imageData.fileName,
              fileSize: imageData.fileSize,
              file: imageData.file,
            });
          } else if (imageData.file && !imageData.isExisting) {
            // New image uploaded
            detailImages.push({
              previewUrl: imageData.previewUrl,
              fileName: imageData.fileName,
              fileSize: imageData.fileSize,
              file: imageData.file,
            });
          }
        }
      }

      // Submit array of detail images to backend
      if (detailImages.length > 0) {
        processedData.detailImages = detailImages;
      }

      // Legacy support - also set the first detail image for backward compatibility
      if (detailImages.length > 0) {
        processedData.detailImage = detailImages[0];
      }

      // Add removed image tracking
      const removedMainImages = this.removedMainImages();
      const removedDetailImages = this.removedDetailImages();

      if (removedMainImages.length > 0) {
        processedData.removedMainImages = removedMainImages;
      }

      if (removedDetailImages.length > 0) {
        processedData.removedDetailImages = removedDetailImages;
      }
    } else {
      // Create mode - process images normally
      const mainImageData = this.mainImageData();
      if (mainImageData) {
        processedData.mainImage = {
          previewUrl: mainImageData.previewUrl,
          fileName: mainImageData.fileName,
          fileSize: mainImageData.fileSize,
          file: mainImageData.file,
        };
      }

      // Handle detail images (indexed controls) for create mode
      const detailImages: any[] = [];
      const maxFiles =
        this.uploadConfigIntegration.getMaxFilesForDetailImages() || 5;

      for (let i = 0; i < maxFiles; i++) {
        const controlName = `detailImage_${i}`;
        const imageData = formData[controlName];

        if (imageData && imageData.file) {
          const imageItem = {
            previewUrl: imageData.previewUrl,
            fileName: imageData.fileName,
            fileSize: imageData.fileSize,
            file: imageData.file,
          };
          detailImages.push(imageItem);
        }
      }

      // Submit array of detail images to backend
      if (detailImages.length > 0) {
        processedData.detailImages = detailImages;
      }

      // Legacy support - also set the first detail image for backward compatibility
      if (detailImages.length > 0) {
        processedData.detailImage = detailImages[0];
      }
    }

    return processedData;
  }

  onFormValueChanges(value: any): void {
    this.formData.set(value);
    // Handle image uploads (following SPU pattern exactly)
    if (value.mainImage) {
      if (
        this.editMode() &&
        !value.mainImage.isExisting &&
        value.mainImage.onFileChanged
      ) {
        this.trackInitialMainImageForRemoval();
      }
      this.mainImageData.set(value.mainImage);
    }

    // Handle usage-instruction video
    if (value.usageVideo) {
      this.removeUsageVideo.set(false);
      this.usageVideoData.set(value.usageVideo);
    }

    if (value.usageVideoType && value.usageVideoType !== this.usageVideoType()) {
      this.usageVideoType.set(value.usageVideoType);
      this.applyUsageVideoTypeVisibility(value.usageVideoType);
    }

    if (
      value.usageVideoPublishStatus &&
      value.usageVideoPublishStatus !== this.usageVideoPublishStatus()
    ) {
      this.usageVideoPublishStatus.set(value.usageVideoPublishStatus);
    }

    // Handle indexed detail images
    const maxFiles =
      this.uploadConfigIntegration.getMaxFilesForDetailImages() || 5;
    for (let i = 0; i < maxFiles; i++) {
      const controlName = `detailImage_${i}`;
      if (value[controlName]) {
        // For now, we'll use the first detail image for backward compatibility
        if (i === 0) {
          this.detailImageData.set(value[controlName]);
        }
        break; // Only handle the first one for now
      }
    }
  }

  // Handle image removal events from file upload components
  onMainImageRemoved(fileName: string): void {
    if (!fileName) return;

    const currentRemoved = this.removedMainImages();
    if (!currentRemoved.includes(fileName)) {
      this.removedMainImages.set([...currentRemoved, fileName]);
    }
  }

  onDetailImageRemoved(fileName: string): void {
    if (!fileName) return;

    const currentRemoved = this.removedDetailImages();
    if (!currentRemoved.includes(fileName)) {
      this.removedDetailImages.set([...currentRemoved, fileName]);
    }
  }

  // Handle image removal events from add-form component
  onImageRemoved(event: {
    fieldName: string;
    fileName: string;
    index?: number;
  }): void {
    if (event.fieldName === 'mainImage') {
      const initialMainImageFileName = this.getInitialMainImageFileName();
      this.onMainImageRemoved(initialMainImageFileName || event.fileName);
      this.mainImageData.set(null); // Clear the main image data since it was removed
    } else if (event.fieldName === 'detailImage') {
      this.onDetailImageRemoved(event.fileName);
      // Don't clear detailImageData signal - it's used for backward compatibility
      // The indexed controls will handle the individual image removal
    } else if (event.fieldName === 'usageVideo') {
      this.removeUsageVideo.set(true);
      this.usageVideoData.set(null);
    }
  }

  // Handle multi-file upload events
  onMultiFileUpload(event: { fieldName: string; fileData: any[] }): void {
    const { fieldName, fileData: files } = event;

    if (fieldName === 'detailImages') {
      // Store the files for later processing
      this.detailImagesData.set(files);
    }
  }

  // Handle drag-drop reordering of existing images within "Thư viện ảnh" (detailImages).
  // Only staged locally here; actually persisted when the user clicks Save
  // (see persistPendingDetailImageReorder, called from saveMaterial's success handler).
  onExistingImagesReordered(event: { fieldName: string; files: any[] }): void {
    const { fieldName, files } = event;

    if (fieldName !== 'detailImages') {
      return;
    }

    // The object key (S3) has no folder prefix (see FileStorageService.getObjectName /
    // constructFileUrl), so fileName — always derived from the image URL for existing detail
    // images — is exactly the object key. Using it directly avoids an extra round-trip.
    const updates: MaterialImageReorderDTO[] = files
      .filter((f) => f.isExisting && !!f.fileName)
      .map((f, index) => ({
        objectName: f.fileName,
        slot: MaterialImageReorderDTO.SlotEnum.Detail,
        newSlot: MaterialImageReorderDTO.NewSlotEnum.Detail,
        sortOrder: index,
      }));

    this.pendingDetailImageReorder.set(updates.length > 0 ? updates : null);
  }

  // Persists a staged drag-drop reorder (if any) after the main Save succeeded. Failure here
  // doesn't affect the already-successful material save; just shows its own error toast.
  private persistPendingDetailImageReorder(materialId: number): void {
    const updates = this.pendingDetailImageReorder();
    if (!updates) {
      return;
    }
    this.pendingDetailImageReorder.set(null);

    this.materialControllerService
      .reorderImages(materialId, updates)
      .subscribe({
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'material.form.upload.reorder_error',
            ),
          });
        },
      });
  }

  private applyUsageVideoTypeVisibility(type: UsageVideoType): void {
    const currentConfig = this.config();
    const isExternal = type === 'EXTERNAL_LINK';

    const updatedFormFields = currentConfig.formFields.map((field) => {
      if (field.controlName === 'usageVideoExternalUrl') {
        return { ...field, hidden: !isExternal, disabled: !isExternal };
      }
      if (field.controlName === 'usageVideo') {
        return { ...field, hidden: isExternal, disabled: isExternal };
      }
      return field;
    });

    this.config.set({ ...currentConfig, formFields: updatedFormFields });
  }

  // Upload configuration methods
  private loadUploadConfiguration(): void {
    const config = this.uploadConfigIntegration.getUploadConfigForCurrentPath();
    if (config) {
      this.uploadConfig.set(config);
      this.applyUploadConfigToForm(config);
    }
  }

  private applyUploadConfigToForm(config: UploadDetailConfig): void {
    // Apply upload configuration to form fields using the integration service
    const currentConfig = this.config();

    const updatedFormFields =
      this.uploadConfigIntegration.applyUploadConfigToFormFields(
        currentConfig.formFields,
      );

    // Create a completely new config object to ensure change detection
    const newConfig: MaterialFormConfig = {
      ...currentConfig,
      formFields: [...updatedFormFields], // Create new array reference
    };

    // Update the config with the modified form fields
    this.config.set(newConfig);
  }

  private interpolateMessage(messageKey: string, params?: any): string {
    return this.translateService.instant(messageKey, params);
  }

  private extractFileNameFromUrl(url: string): string {
    if (!url) return 'unknown-file';

    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const fileName = pathname.split('/').pop() || 'unknown-file';

      // Remove query parameters and hash
      return fileName.split('?')[0].split('#')[0];
    } catch (error) {
      // If URL parsing fails, try to extract filename from the string
      const parts = url.split('/');
      const fileName = parts.at(-1) || 'unknown-file';
      return fileName.split('?')[0].split('#')[0];
    }
  }

  private trackInitialMainImageForRemoval(): void {
    const initialMainImageFileName = this.getInitialMainImageFileName();
    if (initialMainImageFileName) {
      this.onMainImageRemoved(initialMainImageFileName);
    }
  }

  private getInitialMainImageFileName(): string | undefined {
    const initialMainImage = this.initialData()?.mainImage;
    return Array.isArray(initialMainImage)
      ? initialMainImage[0]?.fileName
      : initialMainImage?.fileName;
  }
}
